import { IRCConnectionState, IRCMessageType, tabType } from '#/types/types';
import { Client, User } from 'irc-framework';
import { EventManager } from '#/eventListeners/_eventManager';
import { MAX_RECONNECT_ATTEMPTS } from '#/constants';
import { saveSettings } from '#/util/settings';

export default class ConnectionHandler {
    public ircClient: any;
    public client: Client | null = null;
    public _isConnected: boolean = false;
    public reconnectAttempts: number = 0;
    public autoJoinChannels: string[] = [];
    public tabManager: any;
    public messageHandler: any;
    public eventManager: any;

    constructor(ircClient: any) {
        this.ircClient = ircClient;
        this.tabManager = ircClient.tabManager;
        this.messageHandler = ircClient.messageHandler;
    }

    get isConnected(): boolean {
        return this._isConnected;
    }

    connect(): boolean {
        const serverInput = document.getElementById('irc-server') as HTMLInputElement;
        const portInput = document.getElementById('irc-port') as HTMLInputElement;
        const nickInput = document.getElementById('irc-nickname') as HTMLInputElement;
        const channelsInput = document.getElementById('irc-channels') as HTMLInputElement;
        const passwordInput = document.getElementById('irc-nickserv-password') as HTMLInputElement;
        this.tabManager.addTab(tabType.STATUS);

        if (!serverInput || !portInput || !nickInput) {
            this.ircClient.messageHandler.addMessage(this.tabManager.getStatusTab(), 'Connection form elements not found', IRCMessageType.ERROR);
            return false;
        }

        const server = serverInput.value.trim();
        const port = parseInt(portInput.value.trim());
        const nick = nickInput.value.trim();
        const channelsText = channelsInput ? channelsInput.value.trim() : '';
        const password = passwordInput ? passwordInput.value.trim() : '';

        if (!server || !nick) {
            this.ircClient.messageHandler.addMessage(this.tabManager.getStatusTab(), 'Please fill in all required connection fields', IRCMessageType.ERROR);
            return false;
        }

        const channels = channelsText.split(',')
            .map(channel => {
                const trimmed = channel.trim();
                return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
            })
            .filter(channel => channel.length > 1);

        this.ircClient.settings = { server, port, nickname: nick, channels, nickservPassword:password };
        saveSettings(this.ircClient.settings);

        this.ircClient.messageHandler.addMessage(this.tabManager.getStatusTab(), `Connecting to ${server}:${port} as ${nick}...`, IRCMessageType.SYSTEM);

        const targetTab = this.tabManager.getStatusTab();
        this.tabManager.setActiveTab(targetTab);

        try {
            this.connectViaWebSocket(server, port, nick, channels);
            return true;
        } catch (error) {
            this.ircClient.messageHandler.addMessage(this.tabManager.getStatusTab(), `Connection failed: ${(error as Error).message}`, IRCMessageType.ERROR);
            return false;
        }
    }

    disconnect(): void {
        if (this.client) {
            this.client.quit('LostHQ IRC Client');
        }
        this.client = null;
        this._isConnected = false;
        this.updateButtonStates();
    }

    private connectViaWebSocket(server: string, port: number, nick: string, channels: string[]): void {
        this.autoJoinChannels = channels;

        this.client = new Client({
            host: server,
            port: port,
            nick: nick,
            username: nick,
            gecos: nick,
            encoding: 'utf8',
            auto_reconnect: true,
            auto_reconnect_max_retries: MAX_RECONNECT_ATTEMPTS,
            auto_reconnect_delay: 3000,
            tls: true,
            path: ''
        });

        this.eventManager = new EventManager(this);
        this.client.connect();
    }



    sendIRCCommand(command: string, params?: string | string[]): boolean {
        if (!this.client || (!this._isConnected && command !== 'NICK')) {
            return false;
        }

        try {
            if (params) {
                this.client.raw(command, ...(Array.isArray(params) ? params : [params]));
            } else {
                this.client.raw(command);
            }
            return true;
        } catch (error) {
            this.ircClient.messageHandler.addMessage(this.tabManager.getStatusTab(), `Command failed: ${(error as Error).message}`, IRCMessageType.ERROR);
            return false;
        }
    }

    sendRaw(message: string): boolean {
        if (!this.client || !this._isConnected) {
            return false;
        }

        try {
            this.client.raw(message);
            return true;
        } catch (error) {
            this.ircClient.messageHandler.addMessage(this.tabManager.getStatusTab(), `Raw command failed: ${(error as Error).message}`, IRCMessageType.ERROR);
            return false;
        }
    }

    sendMessage(target: string, message: string): boolean {
        if (!this.client || !this._isConnected) {
            return false;
        }

        try {
            this.client.say(target, message);
            return true;
        } catch (error) {
            this.ircClient.messageHandler.addMessage(this.tabManager.getStatusTab(), `Message send failed: ${(error as Error).message}`, IRCMessageType.ERROR);
            return false;
        }
    }

    joinChannel(channel: string, key?: string): boolean {
        if (!this.client || !this._isConnected) {
            return false;
        }

        try {
            if (key) {
                this.client.join(channel, key);
            } else {
                this.client.join(channel);
            }
            return true;
        } catch (error) {
            this.ircClient.messageHandler.addMessage(this.tabManager.getStatusTab(), `Join failed: ${(error as Error).message}`, IRCMessageType.ERROR);
            return false;
        }
    }

    partChannel(channel: string, reason?: string): boolean {
        if (!this.client || !this._isConnected) {
            return false;
        }

        try {
            this.client.part(channel, reason || 'Leaving');
            return true;
        } catch (error) {
            this.ircClient.messageHandler.addMessage(this.tabManager.getStatusTab(), `Part failed: ${(error as Error).message}`, IRCMessageType.ERROR);
            return false;
        }
    }

    updateButtonStates(): void {
        const connectBtn = document.getElementById('irc-connect') as HTMLButtonElement;
        const disconnectBtn = document.getElementById('irc-disconnect') as HTMLButtonElement;

        if (connectBtn && disconnectBtn) {
            connectBtn.disabled = this._isConnected;
            disconnectBtn.disabled = !this._isConnected;
        }
    }

    getConnectionState(): IRCConnectionState {
        return {
            isConnected: this._isConnected,
            reconnectAttempts: this.reconnectAttempts,
            maxReconnectAttempts: MAX_RECONNECT_ATTEMPTS
        };
    }

    public refreshUserList(channel: string): void {
        if (!this.client) return;

        const channelObj = this.client.channel(channel);
        if (channelObj && channelObj.users) {
            let processedUsers: User[] = [];

            if (channelObj.users instanceof Map) {
                processedUsers = Array.from(channelObj.users.values());
            } else if (Array.isArray(channelObj.users)) {
                processedUsers = channelObj.users;
            } else {
                if (this.client) {
                    this.client.raw('NAMES', channel);
                }
                return;
            }

            if (processedUsers.length === 0) {
                if (this.client) {
                    this.client.raw('NAMES', channel);
                }
                return;
            }

            const tab = this.tabManager.getTab(tabType.CHANNEL, channel);
            if (tab) {
                this.ircClient.messageHandler.updateUserList(tab, processedUsers);
            }
        }
    }
}